import React from "react";
import styled, { keyframes } from "styled-components";

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const SkeletonWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const SkeletonItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const SkeletonItem = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr 80px 120px 50px;
  gap: 25px;
  padding: 20px;
  border: 1px solid #eee;
  border-radius: 8px;
  align-items: center;

  @media (max-width: 992px) {
    grid-template-columns: 100px 1fr 80px;
    gap: 15px;
    padding: 15px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 100px 1fr;
    gap: 20px 15px;
    padding: 15px;
    align-items: start;
  }
`;

const SkeletonImage = styled.div`
  width: 120px;
  height: 120px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 1000px 100%;
  animation: ${shimmer} 2s infinite;
  border-radius: 8px;

  @media (max-width: 768px) {
    width: 100px;
    height: 100px;
  }
`;

const SkeletonDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SkeletonLine = styled.div`
  height: ${(props) => props.$height || "16px"};
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 1000px 100%;
  animation: ${shimmer} 2s infinite;
  border-radius: 4px;
  width: ${(props) => props.$width || "100%"};
`;

const SkeletonQuantity = styled.div`
  width: 50px;
  height: 40px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 1000px 100%;
  animation: ${shimmer} 2s infinite;
  border-radius: 6px;
`;

const SkeletonTotal = styled.div`
  width: 80px;
  height: 20px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 1000px 100%;
  animation: ${shimmer} 2s infinite;
  border-radius: 4px;
  margin-left: auto;
`;

const SkeletonDelete = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 1000px 100%;
  animation: ${shimmer} 2s infinite;
  border-radius: 4px;
  margin-left: auto;
`;

const SkeletonSummary = styled.div`
  background: #f9f9f9;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 30px;
  height: fit-content;
  position: sticky;
  top: 20px;
  display: flex;
  flex-direction: column;
  gap: 15px;

  @media (max-width: 768px) {
    position: static;
    padding: 20px;
    margin-top: 20px;
  }
`;

const SkeletonSummaryLine = styled.div`
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 1000px 100%;
  animation: ${shimmer} 2s infinite;
  border-radius: 4px;
  width: 100%;

  &:nth-child(4) {
    height: 20px;
    margin-top: 10px;
    border-top: 2px solid #eee;
    padding-top: 15px;
  }
`;

const SkeletonButton = styled.div`
  height: 48px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 1000px 100%;
  animation: ${shimmer} 2s infinite;
  border-radius: 4px;
  margin-top: 15px;
`;

const CartSkeleton = () => {
  return (
    <SkeletonWrapper>
      <SkeletonItems>
        {[1, 2].map((item) => (
          <SkeletonItem key={item}>
            <SkeletonImage />
            <SkeletonDetails>
              <SkeletonLine $height="18px" $width="80%" />
              <SkeletonLine $height="16px" $width="60%" />
              <SkeletonLine $height="14px" $width="40%" />
              <SkeletonLine $height="14px" $width="40%" />
            </SkeletonDetails>
            <SkeletonQuantity />
            <SkeletonTotal />
            <SkeletonDelete />
          </SkeletonItem>
        ))}
      </SkeletonItems>

      <SkeletonSummary>
        <SkeletonSummaryLine />
        <SkeletonSummaryLine />
        <SkeletonSummaryLine />
        <SkeletonSummaryLine />
        <SkeletonButton />
      </SkeletonSummary>
    </SkeletonWrapper>
  );
};

export default CartSkeleton;
